import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Image, Info, Lock, Globe, X, Upload, Check, AlertTriangle, TrendingUp } from 'lucide-react';


import { getActivePage } from '../../../redux/navigation';
import { createGroup } from '../../../redux/userGroups/groups/userGroupies';
import { associateStocksWithGroup } from '../../../redux/userGroups/groupsToStock/groupsToStockActions';
import GroupStocksSection from '../groupPageComponents/GroupStockAssociation';

// Mirrors the backend's GroupCreate schema constraints exactly (schemas/group_schema.py) --
// keep these two in sync if either side changes.
const LIMITS = {
  name: { min: 3, max: 100 },
  description: { min: 10, max: 2000 },
  guidelines: { min: 10, max: 2000 },
};

const CharCount = ({ value, max }) => {
  const len = value.length;
  const nearLimit = len > max * 0.9;
  return (
    <p className={`mt-1 text-xs text-right ${nearLimit ? 'text-amber-500 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
      {len}/{max}
    </p>
  );
};

const FormSection = ({ icon: Icon, title, children, error }) => (
  <div className={`bg-cream-100 dark:bg-gray-800/30 rounded-2xl p-8 border transition-all duration-300 ${
    error ? 'border-red-500/50 bg-red-50 dark:bg-red-900/10' : 'border-cream-300 dark:border-gray-700/50 hover:border-cream-900/20 dark:hover:border-gray-600/50'
  }`}>
    <h2 className="text-xl font-semibold mb-6 flex items-center text-gray-900 dark:text-white">
      <div className={`p-2 rounded-xl mr-3 ${error ? 'bg-red-500/20' : 'bg-blue-500/20'}`}>
        <Icon className={`h-5 w-5 ${error ? 'text-red-400' : 'text-blue-400'}`} />
      </div>
      {title}
    </h2>
    {children}
  </div>
);

const InputField = ({ label, error, children, required = false }) => (
  <div className="mb-6">
    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {error && (
      <div className="mt-2 flex items-center space-x-2 text-red-400">
        <AlertTriangle size={16} />
        <p className="text-sm">{error}</p>
      </div>
    )}
  </div>
);

const PrivacyOption = ({ value, selectedValue, onChange, icon: Icon, title, description, disabled }) => (
  <label className={`flex items-start space-x-4 p-4 border rounded-2xl cursor-pointer transition-all duration-300 ${
    selectedValue === value
      ? 'border-blue-500 bg-blue-100 dark:bg-blue-500/10'
      : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 hover:bg-cream-900/5 dark:hover:bg-gray-800/30'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <input
      type="radio"
      name="privacy"
      value={value}
      checked={selectedValue === value}
      onChange={onChange}
      className="text-blue-500 focus:ring-blue-500 h-5 w-5 mt-0.5"
      disabled={disabled}
    />
    <div className="flex-1">
      <div className="flex items-center space-x-3 mb-2">
        <div className={`p-2 rounded-lg ${selectedValue === value ? 'bg-blue-500/20' : 'bg-cream-100 dark:bg-gray-700/50'}`}>
          <Icon className={`h-4 w-4 ${selectedValue === value ? 'text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} />
        </div>
        <p className={`font-medium ${selectedValue === value ? 'text-blue-600 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}>
          {title}
        </p>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>
    </div>
    {selectedValue === value && (
      <div className="bg-blue-500 rounded-full p-1">
        <Check size={12} className="text-white" />
      </div>
    )}
  </label>
);

const GroupCreateContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { createGroupStatus, createGroupError } = useSelector((state) => state.groups);

  // Arrives from StockGroupsPanel's "no groups yet, create one" CTA on a
  // stock detail page -- group creation itself has no stock field
  // (schemas/group_schema.py's GroupCreate never did), so tracking the
  // stock is a real follow-up API call fired right after creation succeeds.
  const prefillStock = location.state?.prefillStock || null;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacy: 'public',
    image: null,
    guidelines: ''
  });
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Always derived fresh from current formData -- never stored in its own state,
  // so it can never go stale. The previous version only recomputed this inside
  // handleSubmit, which meant the submit button (disabled whenever any error
  // existed) could never re-enable itself after the user fixed the problem,
  // since fixing it never re-ran validation and the disabled button could no
  // longer be clicked to trigger a re-check -- a permanent soft-lock.
  const errors = useMemo(() => {
    const newErrors = {};
    const name = formData.name.trim();
    const description = formData.description.trim();
    const guidelines = formData.guidelines.trim();

    if (!name) {
      newErrors.name = t('groups.create.nameRequired');
    } else if (name.length < LIMITS.name.min) {
      newErrors.name = t('groups.create.nameMinLength', { min: LIMITS.name.min });
    } else if (formData.name.length > LIMITS.name.max) {
      newErrors.name = t('groups.create.nameMaxLength', { max: LIMITS.name.max });
    }

    if (!description) {
      newErrors.description = t('groups.create.descriptionRequired');
    } else if (description.length < LIMITS.description.min) {
      newErrors.description = t('groups.create.descriptionMinLength', { min: LIMITS.description.min });
    } else if (formData.description.length > LIMITS.description.max) {
      newErrors.description = t('groups.create.descriptionMaxLength', { max: LIMITS.description.max });
    }

    if (!guidelines) {
      newErrors.guidelines = t('groups.create.guidelinesRequired');
    } else if (guidelines.length < LIMITS.guidelines.min) {
      newErrors.guidelines = t('groups.create.guidelinesMinLength', { min: LIMITS.guidelines.min });
    } else if (formData.guidelines.length > LIMITS.guidelines.max) {
      newErrors.guidelines = t('groups.create.guidelinesMaxLength', { max: LIMITS.guidelines.max });
    }

    return newErrors;
  }, [formData.name, formData.description, formData.guidelines, t]);

  const isValid = Object.keys(errors).length === 0;
  // Only show a field's error once the user has left it (blurred) or tried to
  // submit -- otherwise "Group name is required" would flash before anyone's
  // typed a single character.
  const shownError = (field) => (touched[field] || submitAttempted) ? errors[field] : undefined;
  const handleBlur = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!isValid) return;

    try {
      const groupData = {
        name: formData.name,
        description: formData.description,
        privacy: formData.privacy,
        bio: formData.guidelines,
        ...(formData.image && { image: formData.image })
      };

      const group = await dispatch(createGroup(groupData)).unwrap();

      if (prefillStock?.id) {
        try {
          await dispatch(associateStocksWithGroup({ groupId: group.id, stockIds: [prefillStock.id] })).unwrap();
        } catch (assocError) {
          // The group itself was created successfully -- don't block
          // navigation over the stock-tracking follow-up call failing;
          // the user can still add it manually from the group's Stocks tab.
          console.warn('Group created, but failed to auto-track the prefilled stock:', assocError);
        }
      }

      navigate(`/groups/${group.id}`);
    } catch (error) {
      console.warn('Failed to create group:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setImagePreview(null);
  };

  const isLoading = createGroupStatus === 'loading';

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 dark:from-white dark:to-blue-400 bg-clip-text text-transparent">
              {t('groups.create.heading')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-3 text-lg">
              {t('groups.create.subtitle')}
            </p>
          </div>
          <button
            onClick={() => navigate('/groupies')}
            aria-label={t('common.close')}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-2xl transition-all duration-300 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {prefillStock && (
          <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-600/20 to-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium">
            <TrendingUp className="w-4 h-4" />
            {t('groups.create.trackingStock', { symbol: prefillStock.symbol })}
          </div>
        )}

        {/* Progress indicator */}
        <div className="flex items-center space-x-2 mb-8">
          <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
            <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
                 style={{ width: `${isValid ? 100 : 60}%` }}>
            </div>
          </div>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {isValid ? t('groups.create.readyToCreate') : t('groups.create.completeTheForm')}
          </span>
        </div>
      </div>

      {/* Display creation error */}
      {createGroupError && (
        <div className="mb-8 p-6 bg-red-50 dark:bg-red-900/20 border border-red-500/50 rounded-2xl">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-red-400" size={20} />
            <p className="text-red-600 dark:text-red-400 font-medium">{t('groups.create.errorCreating', { error: createGroupError })}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information Section */}
        <FormSection
          icon={Info}
          title={t('groups.create.basicInformation')}
          error={shownError('name') || shownError('description') || shownError('guidelines')}
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <InputField label={t('groups.create.groupNameLabel')} error={shownError('name')} required>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  onBlur={() => handleBlur('name')}
                  maxLength={LIMITS.name.max}
                  className={`w-full bg-white dark:bg-gray-900/50 border rounded-xl p-4 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 ${
                    shownError('name') ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder={t('groups.create.groupNamePlaceholder')}
                  disabled={isLoading}
                />
                <CharCount value={formData.name} max={LIMITS.name.max} />
              </InputField>
            </div>

            <div className="md:col-span-2">
              <InputField label={t('groups.create.descriptionLabel')} error={shownError('description')} required>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  onBlur={() => handleBlur('description')}
                  maxLength={LIMITS.description.max}
                  className={`w-full bg-white dark:bg-gray-900/50 border rounded-xl p-4 h-32 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none ${
                    shownError('description') ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder={t('groups.create.descriptionPlaceholder')}
                  disabled={isLoading}
                />
                <CharCount value={formData.description} max={LIMITS.description.max} />
              </InputField>
            </div>

            <div className="md:col-span-2">
              <InputField label={t('groups.create.guidelinesLabel')} error={shownError('guidelines')} required>
                <textarea
                  value={formData.guidelines}
                  onChange={(e) => setFormData(prev => ({ ...prev, guidelines: e.target.value }))}
                  onBlur={() => handleBlur('guidelines')}
                  maxLength={LIMITS.guidelines.max}
                  className={`w-full bg-white dark:bg-gray-900/50 border rounded-xl p-4 h-24 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none ${
                    shownError('guidelines') ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder={t('groups.create.guidelinesPlaceholder')}
                  disabled={isLoading}
                />
                <CharCount value={formData.guidelines} max={LIMITS.guidelines.max} />
              </InputField>
            </div>
          </div>
        </FormSection>

        {/* Privacy Settings */}
        <FormSection icon={Lock} title={t('groups.create.privacySettings')}>
          <div className="space-y-4">
            <PrivacyOption
              value="public"
              selectedValue={formData.privacy}
              onChange={(e) => setFormData(prev => ({ ...prev, privacy: e.target.value }))}
              icon={Globe}
              title={t('groups.create.publicGroupTitle')}
              description={t('groups.create.publicGroupDescription')}
              disabled={isLoading}
            />

            <PrivacyOption
              value="private"
              selectedValue={formData.privacy}
              onChange={(e) => setFormData(prev => ({ ...prev, privacy: e.target.value }))}
              icon={Lock}
              title={t('groups.create.privateGroupTitle')}
              description={t('groups.create.privateGroupDescription')}
              disabled={isLoading}
            />
          </div>
        </FormSection>

        {/* Group Image */}
        <FormSection icon={Image} title={t('groups.create.groupAvatar')}>
          <div className="space-y-6">
            {imagePreview ? (
              <div className="relative">
                <div className="w-full max-w-sm mx-auto">
                  <img
                    src={imagePreview}
                    alt={t('groups.create.groupAvatar')}
                    className="w-full h-48 object-cover rounded-2xl border border-gray-300 dark:border-gray-600"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-3 right-3 p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-all duration-300"
                    disabled={isLoading}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-2xl cursor-pointer hover:bg-cream-900/5 dark:hover:bg-gray-800/30 transition-all duration-300 group">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-2xl mb-4 group-hover:bg-gray-200 dark:group-hover:bg-gray-600/50 transition-all duration-300">
                      <Upload className="w-8 h-8 text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300" />
                    </div>
                    <p className="mb-2 text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                      <span className="font-semibold">{t('groups.create.clickToUpload')}</span> {t('groups.create.orDragAndDrop')}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{t('groups.create.imageRequirements')}</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={isLoading}
                  />
                </label>
              </div>
            )}
          </div>
        </FormSection>

        {/* Submit Section */}
        <div className="flex justify-between items-center pt-8 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => navigate('/groupies')}
            className="px-8 py-4 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-300 dark:hover:bg-gray-700 transition-all duration-300 font-medium"
            disabled={isLoading}
          >
            {t('groups.create.cancel')}
          </button>

          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {submitAttempted && !isValid && (
                <span className="text-red-400">{t('groups.create.fixErrors', { count: Object.keys(errors).length })}</span>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !isValid}
              className="px-8 py-4 bg-gradient-to-r from-violet-500 to-blue-600 hover:from-violet-400 hover:to-blue-500 text-white rounded-2xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3 transform hover:scale-105 shadow-lg hover:shadow-violet-500/25"
            >
              {isLoading && (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              <span>{isLoading ? t('groups.create.creating') : t('groups.create.createGroup')}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const GroupCreate = () => {
  return (
    <div className="flex flex-col flex-1 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="flex flex-grow">
        <div className="flex-grow">
          <GroupCreateContent />
        </div>
      </div>
    </div>
  );
};

export default GroupCreate;